import { useEffect, useMemo, useRef, useState } from 'react';
import { searchContext, userAppContext } from '../../../context/Context';
import { useSummaryMatchContext } from '@/modules/search/searchGrid/context/SummaryMatchContext';
import { buildAnalyticsDataRequest } from '../../../utils/buildAnalyticsDataRequest';
import { appendAnalyticsPages, fetchAnalyticsPage } from '../../../utils/analyticsPagination';
import { type AnalyticsTabKey, getAnalyticsConfig } from './analyticsConfig';
import type { AssociationRow } from './analyticsTypes';

const GRAPH_PAGE_SIZE = 250;
const SUMMARY_MATCH_POST_PAGE_SIZE = 10;
const MAX_POST_SUMMARY_PAGES = 6;
const ANALYTICS_FETCH_DEBOUNCE_MS = 250;

export function useAssociationAnalyticsData(
  source: string,
  view: string,
  associationTab: AnalyticsTabKey,
  searchItemsOverride?: Array<{ id: string }>,
) {
  const [data, setData] = useState<AssociationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { authorization, setIsAuthenticated, restrictGridResultCount } = userAppContext() || {};
  const { tab: activeTab, searchItems: contextSearchItems } = searchContext() || {
    tab: 'GDA',
    searchItems: [],
  };
  const summaryMatchContext = useSummaryMatchContext();
  const config = useMemo(() => getAnalyticsConfig(associationTab, view), [associationTab, view]);
  const orderBy = config?.orderBy ?? '';
  const effectiveSearchItems = searchItemsOverride?.length ? searchItemsOverride : contextSearchItems;
  const searchItemsKey = effectiveSearchItems?.map(item => String(item?.id ?? '')).join(',') ?? '';
  const isPanelActive = activeTab === associationTab;
  const resultLimit = typeof restrictGridResultCount === 'number' ? restrictGridResultCount : null;

  const summaryMatchEncodedParams = useMemo(() => {
    if (!summaryMatchContext.isActive) return null;
    if (String(summaryMatchContext.tab ?? '').toUpperCase() !== String(associationTab).toUpperCase()) {
      return null;
    }
    return summaryMatchContext.encodedParams;
  }, [
    associationTab,
    summaryMatchContext.encodedParams,
    summaryMatchContext.isActive,
    summaryMatchContext.tab,
  ]);

  const summaryMatchResultTotal =
    summaryMatchEncodedParams != null ? summaryMatchContext.resultTotalCount : null;

  const usesSummaryMatchPost = summaryMatchEncodedParams != null;
  const pageSize = resultLimit ?? (usesSummaryMatchPost ? SUMMARY_MATCH_POST_PAGE_SIZE : GRAPH_PAGE_SIZE);

  const summaryMatchParamsKey = useMemo(
    () => (summaryMatchEncodedParams ? JSON.stringify(summaryMatchEncodedParams) : ''),
    [summaryMatchEncodedParams],
  );

  const fetchTriggerKey = useMemo(
    () =>
      JSON.stringify({
        source,
        view,
        associationTab,
        searchItemsKey,
        orderBy,
        pageSize,
        resultLimit,
        summaryMatchSessionKey: summaryMatchContext.sessionKey ?? '',
        summaryMatchParamsKey,
        summaryMatchResultTotal,
      }),
    [
      associationTab,
      orderBy,
      pageSize,
      resultLimit,
      searchItemsKey,
      source,
      summaryMatchContext.sessionKey,
      summaryMatchParamsKey,
      summaryMatchResultTotal,
      view,
    ],
  );

  const paramsRef = useRef({
    view,
    source,
    associationTab,
    effectiveSearchItems,
    searchItemsKey,
    orderBy,
    pageSize,
    resultLimit,
    authorization,
    setIsAuthenticated,
    summaryMatchEncodedParams,
    summaryMatchResultTotal,
    config,
    isPanelActive,
  });

  paramsRef.current = {
    view,
    source,
    associationTab,
    effectiveSearchItems,
    searchItemsKey,
    orderBy,
    pageSize,
    resultLimit,
    authorization,
    setIsAuthenticated,
    summaryMatchEncodedParams,
    summaryMatchResultTotal,
    config,
    isPanelActive,
  };

  useEffect(() => {
    const p = paramsRef.current;

    if (!p.isPanelActive) {
      requestIdRef.current += 1;
      setData([]);
      setIsEmpty(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!p.config || !p.searchItemsKey) {
      setData([]);
      setIsEmpty(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    setData([]);
    setIsEmpty(false);
    setIsLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const firstRequest = buildAnalyticsDataRequest({
          view: p.view,
          source: p.source,
          tab: p.associationTab,
          searchItems: p.effectiveSearchItems,
          pageNum: 0,
          pageSize: p.pageSize,
          orderByParam: p.orderBy,
          order: 'desc',
          summaryMatchEncodedParams: p.summaryMatchEncodedParams,
        });

        const firstPage = await fetchAnalyticsPage({
          request: firstRequest,
          authorization: p.authorization,
          setIsAuthenticated: p.setIsAuthenticated,
        });

        if (cancelled || requestId !== requestIdRef.current) return;

        const firstData = firstPage.rows as AssociationRow[];
        if (!firstData.length) {
          setData([]);
          setIsEmpty(true);
          return;
        }

        const fetchPage = async (pageNum: number) => {
          const pageRequest = buildAnalyticsDataRequest({
            view: p.view,
            source: p.source,
            tab: p.associationTab,
            searchItems: p.effectiveSearchItems,
            pageNum,
            pageSize: p.pageSize,
            orderByParam: p.orderBy,
            order: 'desc',
            summaryMatchEncodedParams: p.summaryMatchEncodedParams,
          });

          return fetchAnalyticsPage({
            request: pageRequest,
            authorization: p.authorization,
            setIsAuthenticated: p.setIsAuthenticated,
          });
        };

        let completeData = firstData;

        if (!p.resultLimit) {
          const totalElements = Number(
            firstPage.filteredTotal != null ? firstPage.filteredTotal : firstPage.rawTotal,
          );

          completeData = (await appendAnalyticsPages({
            isSummaryMatchPost: Boolean(p.summaryMatchEncodedParams),
            pageSize: p.pageSize,
            firstPageRows: firstData,
            fetchPage,
            knownTotalCount: p.summaryMatchResultTotal,
            maxPostPages: MAX_POST_SUMMARY_PAGES,
            getTotalElements: totalElements,
          })) as AssociationRow[];
        } else {
          completeData = firstData.slice(0, p.resultLimit);
        }

        if (cancelled || requestId !== requestIdRef.current) return;

        setData(completeData);
        setIsEmpty(false);
      } catch (fetchError) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setError(fetchError instanceof Error ? fetchError.message : 'Error al cargar los datos');
        setData([]);
        setIsEmpty(true);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    debounceRef.current = setTimeout(() => {
      void loadData();
    }, ANALYTICS_FETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;
    };
  }, [fetchTriggerKey, isPanelActive]);

  return { data, isLoading, isEmpty, error, config, isPanelActive };
}

export { useAssociationAnalyticsData as useGdaAnalyticsData };
